import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { CANDIDATE_NAV } from "./nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, FileText, CheckCircle2, Bookmark, CalendarClock, ArrowRight, Upload, Download, Trash2 } from "lucide-react";

const STATUS_COLORS = {
  Applied: "bg-blue-50 text-blue-700", "Under Review": "bg-amber-50 text-amber-700",
  Shortlisted: "bg-purple-50 text-purple-700", Interview: "bg-indigo-50 text-indigo-700",
  Selected: "bg-emerald-50 text-emerald-700", Rejected: "bg-red-50 text-red-700",
};

export default function CandidateDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const reloadProfile = () => api.get("/candidates/me").then((r) => setProfile(r.data));

  const uploadResume = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/candidates/me/resume", fd);
      await reloadProfile();
      toast.success("Resume uploaded & profile auto-filled with AI ✨");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteResume = async () => { await api.delete("/candidates/me/resume"); await reloadProfile(); toast.success("Resume removed"); };

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/candidates/me").then((r) => {
      setProfile(r.data);
      const skill = (r.data.skills || [])[0];
      api.get("/jobs", { params: { skill, page_size: 4 } }).then((res) => setRecommended(res.data.jobs)).catch(() => {});
    }).catch(() => {});
  }, []);

  const toggleVisibility = async () => {
    const v = !profile.profile_visibility;
    await api.put("/candidates/me", { profile_visibility: v });
    setProfile((p) => ({ ...p, profile_visibility: v }));
    toast.success(v ? "Profile is now searchable" : "Profile set to private");
  };

  const stats = [
    { label: "Profile Views", value: data?.profile_views ?? 0, icon: Eye },
    { label: "Applications", value: data?.applications ?? 0, icon: FileText },
    { label: "Shortlisted", value: data?.shortlisted ?? 0, icon: CheckCircle2 },
    { label: "Saved Jobs", value: data?.saved_jobs ?? 0, icon: Bookmark },
    { label: "Interviews", value: data?.interviews ?? 0, icon: CalendarClock },
  ];

  return (
    <DashboardShell nav={CANDIDATE_NAV}>
      <span className="inline-block text-xs font-mono-tech uppercase tracking-widest text-[#D96B27] bg-[#FFF3EC] px-2.5 py-1 rounded-full mb-1" data-testid="candidate-role-chip">Candidate Account</span>
      <h1 className="font-display text-2xl font-bold text-slate-900">Welcome, {user?.name?.split(" ")[0] || "there"} 👋</h1>
      <p className="text-slate-500 text-sm mt-1">Here's what's happening with your job search.</p>

      {profile && profile.completeness < 100 && (
        <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="profile-completeness">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-semibold text-slate-900">Profile Completeness</span>
            <span className="font-mono-tech font-bold text-[#D96B27]">{profile.completeness}%</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full copper-gradient rounded-full transition-all" style={{ width: `${profile.completeness}%` }} />
          </div>
          <Link to="/candidate/profile" className="inline-flex items-center gap-1 text-sm text-[#D96B27] font-medium mt-3 hover:gap-2 transition-all">Complete your profile <ArrowRight className="w-4 h-4" /></Link>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <s.icon className="w-5 h-5 text-[#D96B27]" />
            <div className="font-display text-2xl font-bold text-slate-900 mt-2">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-5 mt-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-display font-semibold text-slate-900 mb-3">Recent Applications</h2>
          {data?.recent_applications?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100"><th className="py-2 font-medium">Company</th><th className="py-2 font-medium">Job</th><th className="py-2 font-medium">Applied</th><th className="py-2 font-medium">Status</th></tr></thead>
                <tbody>
                  {data.recent_applications.map((a) => (
                    <tr key={a.application_id} className="border-b border-slate-50">
                      <td className="py-2.5 text-slate-700">{a.company_name}</td>
                      <td className="py-2.5 text-slate-700">{a.job_title}</td>
                      <td className="py-2.5 text-slate-400">{a.applied_at?.slice(0, 10)}</td>
                      <td className="py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[a.status] || "bg-slate-100"}`}>{a.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-slate-400 py-6 text-center">No applications yet. <Link to="/jobs" className="text-[#D96B27]">Browse jobs →</Link></p>}
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="resume-card">
            <h2 className="font-display font-semibold text-slate-900 mb-1 text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-[#D96B27]" />Resume</h2>
            {profile?.resume ? (
              <div>
                <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2.5 mt-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{profile.resume.filename}</div>
                    <div className="text-xs text-slate-400">Updated {profile.resume.uploaded_at?.slice(0, 10)}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <a href={`${api.defaults.baseURL}/files/${profile.resume.storage_path}?download=true`} target="_blank" rel="noreferrer" data-testid="download-resume-btn" className="p-1.5 text-slate-500 hover:text-[#D96B27]"><Download className="w-4 h-4" /></a>
                    <button onClick={deleteResume} data-testid="dash-delete-resume-btn" className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <button onClick={() => fileRef.current.click()} data-testid="dash-replace-resume-btn" className="text-xs text-[#D96B27] font-medium mt-2">{uploading ? "Uploading…" : "Replace resume"}</button>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-xs text-slate-500 mb-2">No resume uploaded. Upload to auto-fill your profile with AI.</p>
                <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="dash-upload-resume-btn" className="w-full flex items-center justify-center gap-2 text-sm font-semibold copper-gradient text-white rounded-lg py-2.5 disabled:opacity-60">
                  <Upload className="w-4 h-4" />{uploading ? "Uploading & parsing…" : "Upload Resume"}
                </button>
                <p className="text-xs text-slate-400 mt-1.5 text-center">PDF, DOC or DOCX · max 5MB</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={uploadResume} />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-display font-semibold text-slate-900 mb-1 text-sm">Profile Visibility</h2>
            <p className="text-xs text-slate-500 mb-3">Let recruiters discover you in the candidate database.</p>
            <button data-testid="visibility-toggle" onClick={toggleVisibility} className="flex items-center justify-between w-full">
              <span className="text-sm text-slate-700">{profile?.profile_visibility ? "Searchable" : "Private"}</span>
              <span className={`w-11 h-6 rounded-full p-0.5 transition-colors ${profile?.profile_visibility ? "bg-[#D96B27]" : "bg-slate-300"}`}>
                <span className={`block w-5 h-5 bg-white rounded-full transition-transform ${profile?.profile_visibility ? "translate-x-5" : ""}`} />
              </span>
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-display font-semibold text-slate-900 mb-3 text-sm">Recommended Jobs</h2>
            {recommended.length ? recommended.map((j) => (
              <Link key={j.job_id} to={`/jobs/${j.job_id}`} className="block py-2.5 border-b border-slate-50 last:border-0 hover:text-[#D96B27]">
                <div className="font-medium text-sm text-slate-800">{j.title}</div>
                <div className="text-xs text-slate-400">{j.company_name} · {j.location}</div>
              </Link>
            )) : <p className="text-xs text-slate-400">Add skills to your profile for recommendations.</p>}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
